import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { differenceInCalendarDays, format } from 'date-fns';
import { useLingui } from '@lingui/react';

import { BaseEditableForm } from '@/components/form/BaseEditableForm';
import type { FieldConfig, GroupConfig, EnhancedSelectOption } from '@/components/form/types';
import { parseDateString } from '@/utils/dateUtils';
import { useCreateChangeRequest } from '@/hooks/useChildDataUpdateRequest';
import {
  CARE_DAY_SUB_TYPES,
  buildCareDayBatch,
  getSubTypeLabel,
  getWeekdayLabel,
  type CareDayRequestFormValues,
  type ChangeRequestSourceKind,
} from '@/types/changeRequest';
import type { change_requests_ChangeRequestResultDTO } from '@/api';
import { typeError } from '@/lib/zodTypeError';

interface CareDayRequestFormProps {
  /** The contract group the care-day change targets (from the Contract editor). */
  contractGroupId: string;
  /** Optional explicit target version; when absent the executor resolves the covering version. */
  versionId?: string;
  /** Contract active window — dates must fall within [from, to] when both are provided. */
  activeRangeFrom?: string;
  activeRangeTo?: string;
  /** Producer source — only the kind differs across channels; defaults to web_admin. */
  source?: { kind: ChangeRequestSourceKind; message_id?: string };
  /** Receives the per-request result after a successful create. */
  onResult?: (result: change_requests_ChangeRequestResultDTO | undefined) => void;
  onSuccess?: () => void;
  onCancel?: () => void;
}

type FormValues = CareDayRequestFormValues;

// ISO weekday values 1..7 — a bounded select enforces the 1..7 range at the UI so
// an out-of-range weekday can never be posted.
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * Create a `care_day` (add_day / remove_day) change request from the Contract
 * editor. Built on BaseEditableForm; posts through the producer-agnostic
 * {@link useCreateChangeRequest} hook (same path as the children / email / app
 * producers — only `family` + `executor.body` differ). The 5-day SBU attendance
 * cap is enforced server-side (it depends on the current weekday set the form
 * doesn't hold); an over-cap / invalid rejection surfaces via the hook's routing
 * toast + the CHANGE_REQUEST_UNSUPPORTED_SUBTYPE error mapping.
 */
export function CareDayRequestForm({
  contractGroupId,
  versionId,
  activeRangeFrom,
  activeRangeTo,
  source,
  onResult,
  onSuccess,
  onCancel,
}: CareDayRequestFormProps) {
  const { i18n } = useLingui();
  const containerRef = useRef<HTMLDivElement>(null);

  const create = useCreateChangeRequest();

  const schema = useMemo(
    () =>
      z
        .object({
          subType: z.enum(CARE_DAY_SUB_TYPES),
          weekday: z
            .string()
            .refine((v) => WEEKDAYS.some((d) => String(d) === v), {
              message: i18n._({
                id: 'changeRequest.validation.weekdayRange',
                message: 'Choose a weekday from Monday to Sunday',
              }),
            }),
          activeFrom: z.date({
            error: typeError({
              required: i18n._({ id: 'changeRequest.validation.required', message: 'Required' }),
              invalid: i18n._({ id: 'changeRequest.validation.required', message: 'Required' }),
            }),
          }),
          activeTo: z.date({
            error: typeError({
              required: i18n._({ id: 'changeRequest.validation.required', message: 'Required' }),
              invalid: i18n._({ id: 'changeRequest.validation.required', message: 'Required' }),
            }),
          }),
          reason: z.string().optional().default(''),
        })
        .superRefine((val, ctx) => {
          if (val.activeFrom && val.activeTo && val.activeTo.getTime() < val.activeFrom.getTime()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['activeTo'],
              message: i18n._({
                id: 'changeRequest.validation.windowOrder',
                message: 'The end date must be on or after the start date',
              }),
            });
          }
          const rangeFrom = parseDateString(activeRangeFrom);
          const rangeTo = parseDateString(activeRangeTo);
          const withinMessage = i18n._({
            id: 'changeRequest.validation.withinContractRange',
            message: "The dates must fall within the contract's active range",
          });
          if (rangeFrom && val.activeFrom && differenceInCalendarDays(val.activeFrom, rangeFrom) < 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['activeFrom'], message: withinMessage });
          }
          if (rangeTo && val.activeTo && differenceInCalendarDays(val.activeTo, rangeTo) > 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['activeTo'], message: withinMessage });
          }
        }),
    [i18n, activeRangeFrom, activeRangeTo],
  ) as unknown as z.ZodType<FormValues, FormValues>;

  const fields: FieldConfig<FormValues>[] = useMemo(() => {
    const subTypeOptions: EnhancedSelectOption[] = CARE_DAY_SUB_TYPES.map((st) => ({
      value: st,
      label: getSubTypeLabel(i18n, st),
    }));
    const weekdayOptions: EnhancedSelectOption[] = WEEKDAYS.map((d) => ({
      value: String(d),
      label: getWeekdayLabel(i18n, d),
    }));

    return [
      {
        name: 'subType',
        label: i18n._({ id: 'changeRequest.field.subType', message: 'Change type' }),
        type: 'select',
        required: true,
        selectOptions: subTypeOptions,
      },
      {
        name: 'weekday',
        label: i18n._({ id: 'changeRequest.field.weekday', message: 'Weekday' }),
        type: 'select',
        required: true,
        selectOptions: weekdayOptions,
      },
      {
        name: 'activeFrom',
        label: i18n._({ id: 'changeRequest.field.activeFrom', message: 'From' }),
        type: 'date',
        required: true,
      },
      {
        name: 'activeTo',
        label: i18n._({ id: 'changeRequest.field.activeTo', message: 'To' }),
        type: 'date',
        required: true,
      },
      {
        name: 'reason',
        label: i18n._({ id: 'changeRequest.field.reason', message: 'Reason (optional)' }),
        type: 'textarea',
        colSpan: 2,
      },
    ];
  }, [i18n]);

  const groups: GroupConfig<FormValues>[] = useMemo(
    () => [
      {
        title: i18n._({ id: 'changeRequest.group.careDay', message: 'Care-day change' }),
        fields: ['subType', 'weekday', 'activeFrom', 'activeTo', 'reason'] as Array<keyof FormValues>,
      },
    ],
    [i18n],
  );

  const defaultValues: Partial<FormValues> = {
    subType: 'add_day',
    weekday: '1',
    activeFrom: undefined,
    activeTo: undefined,
    reason: '',
  };

  const handleSubmit = async (values: FormValues) => {
    const batch = buildCareDayBatch({
      subType: values.subType,
      contractGroupId,
      versionId,
      weekday: Number(values.weekday),
      activeFrom: values.activeFrom ? format(values.activeFrom, 'yyyy-MM-dd') : '',
      activeTo: values.activeTo ? format(values.activeTo, 'yyyy-MM-dd') : '',
      reason: values.reason,
      source: source ?? { kind: 'web_admin' },
    });

    const res = await create.mutateAsync({ batch, childIds: [] });
    onResult?.(res.results?.[0]);
    onSuccess?.();
  };

  // Focus the first interactive control on mount.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const first = root.querySelector<HTMLElement>(
      'button[role="combobox"]:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled])',
    );
    first?.focus();
  }, []);

  // Cmd/Ctrl+Enter submits the form from anywhere within it.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      const form = containerRef.current?.querySelector('form');
      if (form?.requestSubmit) {
        form.requestSubmit();
      } else {
        containerRef.current?.querySelector<HTMLButtonElement>('button[type="submit"]')?.click();
      }
    }
  };

  return (
    <div className="space-y-4" ref={containerRef} onKeyDown={handleKeyDown}>
      <BaseEditableForm<FormValues>
        schema={schema}
        fields={fields}
        groups={groups}
        defaultValues={defaultValues}
        mode="create"
        onSubmit={handleSubmit}
        onCancel={onCancel}
        isSubmitting={create.isPending}
      />
    </div>
  );
}
